import { useEffect, useState } from 'react';
import { FlexRow, FlexSpacer, Text, IconButton, Panel } from '@epam/promo';
import css from '../../tasks/Comments/Comments.module.scss';
import { ReactComponent as ActionAddFillIcon } from '@epam/assets/icons/action-add-fill.svg';
import { Comment } from '../../tasks/Comments/Comment';
import {
  useBroadcastCommentCreateQuery,
  useBroadcastCommentsGetQuery,
  useBroadcastCommentDeleteQuery,
  useBroadcastCommentUpdateQuery,
} from 'hooks/api/comments';
import { useBroadcastStore } from 'store/broadcast';
import { TComment } from 'models/task-comment.model';
import { useUserStore } from 'store/user/user.store';

const NEW_ID = 'tmp';

const prepareCommentsFromServer = (comments: TComment[]) =>
  comments.map((comment) => ({
    ...comment,
    editMode: false,
  }));

export const BroadcastCommentsColumn = () => {
  const broadcastId = useBroadcastStore((state) => state.draft.id);
  const [comments, setComments] = useState<TComment[]>([]);
  const { data: commentsQueryData } = useBroadcastCommentsGetQuery(broadcastId);
  const { mutate: createComment } = useBroadcastCommentCreateQuery();
  const { mutate: updateComment } = useBroadcastCommentUpdateQuery();
  const { mutate: deleteComment } = useBroadcastCommentDeleteQuery();

  useEffect(() => {
    // Only update if we have valid data from the server
    if (commentsQueryData) {
      setComments(prepareCommentsFromServer(commentsQueryData));
    }
  }, [commentsQueryData]);

  const toggleEditMode = (commentId: string, editMode: boolean) => {
    setComments((comments) =>
      comments.map((comment) =>
        comment.id === commentId ? { ...comment, editMode } : comment,
      ),
    );
  };

  const currentUser = useUserStore((state) => state.user);
  const createNewComment = () => {
    const newComment: TComment = {
      id: `${NEW_ID}_${crypto.randomUUID()}`,
      author: currentUser!,
      authorId: currentUser?.id,
      createdAt: new Date().toISOString(),
      value: '',
      entityId: broadcastId,
      entityType: 'broadcast',
      editMode: true,
    };

    setComments((prev) => [newComment, ...prev]);
  };

  const onConfirmEdit = (commentId: string, newValue: string) => {
    const editedComment = comments.find((comment) => comment.id === commentId);
    const apiFn = editedComment?.id.startsWith(NEW_ID)
      ? createComment
      : updateComment;

    apiFn({
      entityId: broadcastId,
      comment: {
        ...editedComment!,
        value: newValue,
      },
    });
    toggleEditMode(commentId, false);
  };

  const onStartEdit = (commentId: string) => {
    toggleEditMode(commentId, true);
  };

  const onRejectEdit = (commentId: string) => {
    if (commentId.startsWith(NEW_ID)) {
      setComments((prev) => prev.filter((comment) => comment.id !== commentId));
    } else {
      toggleEditMode(commentId, false);
    }
  };

  const onRemove = (commentId: string) => {
    deleteComment({ broadcastId, commentId });
  };

  // Don't render comments column if there's no valid broadcast ID
  if (!broadcastId) {
    return (
      <Panel shadow cx={css.wrapper}>
        <FlexRow cx={css.header} padding="12" vPadding="12">
          <Text fontSize="24" fontWeight="600">
            Comments Editor
          </Text>
        </FlexRow>
        <div className={css.commentsList}>
          <FlexRow padding="24" vPadding="24">
            <Text color="gray60" fontSize="14">
              Save broadcast to enable comments
            </Text>
          </FlexRow>
        </div>
      </Panel>
    );
  }

  const isEditInProcess = comments.some((comment) => comment.editMode);

  return (
    <Panel shadow cx={css.wrapper}>
      <FlexRow cx={css.header} padding="12" vPadding="12">
        <Text fontSize="24" fontWeight="600">
          Comments Editor
        </Text>
        <FlexSpacer />
        <IconButton
          icon={ActionAddFillIcon}
          isDisabled={isEditInProcess}
          onClick={() => {
            createNewComment();
          }}
        />
      </FlexRow>
      <div className={css.commentsList}>
        {comments.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            currentUserId={currentUser?.id}
            isEditInProcess={isEditInProcess}
            onConfirmEdit={onConfirmEdit}
            onRejectEdit={onRejectEdit}
            onRemove={onRemove}
            onStartEdit={onStartEdit}
          />
        ))}
      </div>
    </Panel>
  );
};
